import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMemo, useRef, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  useBatchImportStatus,
  useBatchImportAddresses,
  useCancelBatchImport,
} from "../hooks/usePortfolioQueries"
import type { BatchImportAddress } from "../services/portfolioApi"

// ─── Constants ────────────────────────────────────────────────────────────────

const STUCK_THRESHOLD_MS = 10 * 60 * 1000 // 10 minutes with no progress

// ─── Address status config ────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  BatchImportAddress["status"],
  { label: string; color: string }
> = {
  pending:      { label: "Pending",      color: "gray"   },
  processing:   { label: "Processing",   color: "blue"   },
  analysed:     { label: "Analysed",     color: "green"  },
  failed:       { label: "Failed",       color: "red"    },
  missing_data: { label: "Missing data", color: "orange" },
  cancelled:    { label: "Cancelled",    color: "gray"   },
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ label: string; count: number; color: string }> = ({
  label,
  count,
  color,
}) => (
  <HStack
    bg={`${color}.50`}
    px={3}
    py={1}
    borderRadius="md"
    border="1px solid"
    borderColor={`${color}.200`}
  >
    <Text fontSize="sm" color={`${color}.700`} fontWeight="bold">
      {count}
    </Text>
    <Text fontSize="sm" color={`${color}.600`}>
      {label}
    </Text>
  </HStack>
)

const AddressRow: React.FC<{ address: BatchImportAddress }> = ({ address }) => {
  const cfg = STATUS_CONFIG[address.status] ?? { label: address.status, color: "gray" }
  return (
    <Box
      px={4}
      py={3}
      borderBottom="1px solid"
      borderColor="gray.100"
      _last={{ borderBottom: "none" }}
    >
      <HStack justify="space-between" align="start" gap={4}>
        <Text fontSize="sm" flex={1} color="gray.800" wordBreak="break-word">
          {address.fullAddress}
        </Text>
        <Badge
          colorPalette={cfg.color}
          flexShrink={0}
          fontSize="xs"
          px={2}
          py={0.5}
          borderRadius="md"
        >
          {cfg.label}
        </Badge>
      </HStack>
      {address.errorMessage && (
        <Text fontSize="xs" color="red.500" mt={1}>
          {address.errorMessage}
        </Text>
      )}
    </Box>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

const BatchImportProgress: React.FC = () => {
  const navigate = useNavigate()
  const { batchImportId } = useParams<{ batchImportId: string }>()
  const id = batchImportId ? Number(batchImportId) : null

  const { data, isLoading, error, dataUpdatedAt } = useBatchImportStatus(id)
  const {
    data: addressPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useBatchImportAddresses(id)
  const cancelMutation = useCancelBatchImport(id)

  const [secondsAgo, setSecondsAgo] = useState(0)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [showFailedList, setShowFailedList] = useState(false)
  const [showMissingList, setShowMissingList] = useState(false)

  // Track whether counts have changed to detect stuck imports
  const prevCountsRef = useRef<string | null>(null)
  const stuckSinceRef = useRef<number | null>(null)
  const [isStuck, setIsStuck] = useState(false)

  // Seconds-ago ticker
  useEffect(() => {
    if (!dataUpdatedAt) return
    const tick = () => setSecondsAgo(Math.floor((Date.now() - dataUpdatedAt) / 1000))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [dataUpdatedAt])

  // Stuck detection: flag if counts haven't changed for STUCK_THRESHOLD_MS
  useEffect(() => {
    if (!data || data.status !== "processing") {
      stuckSinceRef.current = null
      setIsStuck(false)
      return
    }

    const countsKey = JSON.stringify(data.counts)
    if (countsKey !== prevCountsRef.current) {
      prevCountsRef.current = countsKey
      stuckSinceRef.current = Date.now()
      setIsStuck(false)
    } else if (stuckSinceRef.current !== null) {
      if (Date.now() - stuckSinceRef.current >= STUCK_THRESHOLD_MS) {
        setIsStuck(true)
      }
    }
  }, [data])

  const isFinished = data?.status === "completed" || data?.status === "cancelled"

  const processed = data
    ? data.counts.analysed + data.counts.failed + data.counts.missingData + data.counts.cancelled
    : 0

  const progressPercent =
    data && data.totalAddresses > 0
      ? Math.round((processed / data.totalAddresses) * 100)
      : 0

  const allAddresses = useMemo(
    () => addressPages?.pages.flatMap((p) => p.results) ?? [],
    [addressPages]
  )

  const failedAddresses = useMemo(
    () => allAddresses.filter((a) => a.status === "failed"),
    [allAddresses]
  )

  const missingAddresses = useMemo(
    () => allAddresses.filter((a) => a.status === "missing_data"),
    [allAddresses]
  )

  const handleCancel = async () => {
    await cancelMutation.mutateAsync()
    setShowCancelConfirm(false)
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        <Box>
          <Button
            variant="ghost"
            onClick={() => navigate("/portfolio")}
            mb={4}
            alignSelf="flex-start"
          >
            ← Back to Portfolio
          </Button>
          <Heading size="xl">Batch Import</Heading>
        </Box>

        {/* Status card */}
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
          {!id && (
            <Text color="red.500">
              Invalid batch import ID. Please try importing again.
            </Text>
          )}

          {id && isLoading && (
            <Text color="gray.500">Loading import status...</Text>
          )}

          {id && error && (
            <Text color="red.500">
              Failed to load import status. Please try again.
            </Text>
          )}

          {id && data && (
            <VStack gap={5} align="stretch">

              {/* Header row */}
              <HStack justify="space-between">
                <HStack gap={3}>
                  <Text fontWeight="bold" fontSize="lg">
                    {data.status === "cancelled"
                      ? "Import cancelled"
                      : isFinished
                      ? "Import complete"
                      : "Importing..."}
                  </Text>
                  {!isFinished && <Spinner size="sm" color="blue.500" />}
                </HStack>
                <Text color="gray.500" fontSize="sm">
                  {processed} / {data.totalAddresses} addresses
                </Text>
              </HStack>

              {/* Progress bar */}
              <Box w="100%" h="10px" bg="gray.200" borderRadius="md" overflow="hidden">
                <Box
                  h="100%"
                  w={`${progressPercent}%`}
                  bg={
                    data.status === "cancelled"
                      ? "gray.400"
                      : isFinished
                      ? "green.500"
                      : "blue.500"
                  }
                  transition="width 0.5s ease"
                />
              </Box>

              {/* Status counts */}
              <HStack gap={4} flexWrap="wrap">
                <StatusBadge label="Analysed"     count={data.counts.analysed}    color="green"  />
                <StatusBadge label="Pending"      count={data.counts.pending}     color="gray"   />
                <StatusBadge label="Processing"   count={data.counts.processing}  color="blue"   />
                <StatusBadge label="Missing data" count={data.counts.missingData} color="orange" />
                <StatusBadge label="Failed"       count={data.counts.failed}      color="red"    />
                {data.counts.cancelled > 0 && (
                  <StatusBadge label="Cancelled" count={data.counts.cancelled} color="gray" />
                )}
              </HStack>

              {/* Last-updated ticker */}
              {!isFinished && (
                <Text color="gray.500" fontSize="sm">
                  Last updated {secondsAgo} {secondsAgo === 1 ? "second" : "seconds"} ago
                </Text>
              )}

              {/* Stuck warning */}
              {isStuck && !isFinished && (
                <Box
                  bg="yellow.50"
                  border="1px solid"
                  borderColor="yellow.300"
                  borderRadius="md"
                  p={4}
                >
                  <Text color="yellow.800" fontWeight="medium" fontSize="sm">
                    This import seems to be taking longer than expected. It may be stuck.
                  </Text>
                  <Text color="yellow.700" fontSize="sm" mt={1}>
                    You can wait a little longer, or cancel it and try again.
                  </Text>
                </Box>
              )}

              {/* Completion summary */}
              {isFinished && data.status !== "cancelled" && (
                <Box
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={4}
                >
                  <Text fontWeight="medium" mb={2}>Summary</Text>
                  <VStack align="start" gap={1}>
                    <Text fontSize="sm" color="green.700">
                      ✓ {data.counts.analysed} home{data.counts.analysed !== 1 ? "s" : ""} added successfully
                    </Text>
                    {data.counts.missingData > 0 && (
                      <Text fontSize="sm" color="orange.700">
                        ⚠ {data.counts.missingData} address{data.counts.missingData !== 1 ? "es" : ""} had missing data
                      </Text>
                    )}
                    {data.counts.failed > 0 && (
                      <Text fontSize="sm" color="red.700">
                        ✕ {data.counts.failed} address{data.counts.failed !== 1 ? "es" : ""} failed
                      </Text>
                    )}
                  </VStack>
                </Box>
              )}

              {/* Action buttons */}
              <HStack gap={3} flexWrap="wrap">
                {isFinished ? (
                  <Button
                    bg="green.800"
                    color="white"
                    _hover={{ bg: "green.700" }}
                    onClick={() => navigate("/portfolio")}
                  >
                    View Portfolio
                  </Button>
                ) : (
                  <>
                    {!showCancelConfirm ? (
                      <Button
                        variant="outline"
                        colorPalette="red"
                        size="sm"
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={cancelMutation.isPending}
                      >
                        Cancel import
                      </Button>
                    ) : (
                      <HStack gap={2}>
                        <Text fontSize="sm" color="gray.700">Are you sure?</Text>
                        <Button
                          size="sm"
                          colorPalette="red"
                          onClick={handleCancel}
                          loading={cancelMutation.isPending}
                        >
                          Yes, cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowCancelConfirm(false)}
                          disabled={cancelMutation.isPending}
                        >
                          Keep going
                        </Button>
                      </HStack>
                    )}
                  </>
                )}
              </HStack>

            </VStack>
          )}
        </Box>

        {/* Address list */}
        {id && allAddresses.length > 0 && (
          <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
            <Box px={6} py={4} borderBottom="1px solid" borderColor="gray.100">
              <Text fontWeight="semibold" fontSize="md">
                Addresses ({addressPages?.pages[0]?.count ?? 0})
              </Text>
            </Box>

            {/* Failed addresses – collapsible */}
            {failedAddresses.length > 0 && (
              <Box borderBottom="1px solid" borderColor="gray.100">
                <HStack
                  px={6}
                  py={3}
                  justify="space-between"
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => setShowFailedList((v) => !v)}
                  bg="red.50"
                >
                  <Text fontSize="sm" fontWeight="medium" color="red.700">
                    Failed ({failedAddresses.length})
                  </Text>
                  <Text fontSize="xs" color="red.500">
                    {showFailedList ? "▲ Hide" : "▼ Show"}
                  </Text>
                </HStack>
                {showFailedList && failedAddresses.map((a) => (
                  <AddressRow key={a.id} address={a} />
                ))}
              </Box>
            )}

            {/* Missing data addresses – collapsible */}
            {missingAddresses.length > 0 && (
              <Box borderBottom="1px solid" borderColor="gray.100">
                <HStack
                  px={6}
                  py={3}
                  justify="space-between"
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => setShowMissingList((v) => !v)}
                  bg="orange.50"
                >
                  <Text fontSize="sm" fontWeight="medium" color="orange.700">
                    Missing data ({missingAddresses.length})
                  </Text>
                  <Text fontSize="xs" color="orange.500">
                    {showMissingList ? "▲ Hide" : "▼ Show"}
                  </Text>
                </HStack>
                {showMissingList && missingAddresses.map((a) => (
                  <AddressRow key={a.id} address={a} />
                ))}
              </Box>
            )}

            {/* All addresses */}
            {allAddresses.map((a) => (
              <AddressRow key={a.id} address={a} />
            ))}

            {/* Load more */}
            {hasNextPage && (
              <Box px={6} py={4} textAlign="center" borderTop="1px solid" borderColor="gray.100">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  loading={isFetchingNextPage}
                >
                  Load more
                </Button>
              </Box>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default BatchImportProgress
